import type React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import styled, { css, useTheme } from "styled-components"
import { Icon, type IconProps } from "../Icon"
import type { CSSCursor } from "../types/css"
import type { BaseProps, BorderProps } from "../types/props"
import { ComponentError } from "../util/components"
import { getBorderStyle } from "../util/props/border"
import { contrastColor, overloadColor } from "../util/style"
import { isFunction, isUndefined } from "../util/typechecks"

type NumberInputProps = {}
const NumberInput = styled.input.attrs({ size: 1 })<NumberInputProps>`
  padding: 0;
  border: 0;
  background: inherit;
  text-align: center;

  &:focus {
    outline: none;
  }
`
interface WrapperProps {
  height: string
  color: string
  fontSize: string
  separatorColor: string
}
const Wrapper = styled.div<WrapperProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  height: ${(props) => props.height};

  ${NumberInput} {
    color: ${(props) => overloadColor(props.color)};
    font-size: ${(props) => props.fontSize};
  }

  span.--time-picker-separator {
    transform: translateY(-2px);
    color: ${(props) => overloadColor(props.separatorColor)};
    font-size: ${(props) => props.fontSize};
  }
`
interface InnerWrapperProps extends BorderProps {
  width?: string
  minWidth?: string
  maxWidth?: string
  iconSide: IconPosition | "both" | "none"
  padding: string
  borderRadius: string
  background: string
}
const InnerWrapper = styled.div<InnerWrapperProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;

  width: ${(props) => props.width};
  min-width: ${(props) => props.minWidth};
  max-width: ${(props) => props.maxWidth};
  height: 100%;
  padding: ${(props) => props.padding};

  background: ${(props) => overloadColor(props.background)};

  ${getBorderStyle}
  border-radius: ${(props) => {
    const radius = props.borderRadius

    switch (props.iconSide) {
      case "left":
        return `0 ${radius} ${radius} 0;`
      case "right":
        return `${radius} 0 0 ${radius};`
      case "both":
        return "0;"
      default:
        return `${radius};`
    }
  }};

  transition: border-color 0.1s linear;

  &:focus-within {
    outline: 0;
    border-color: ${(props) => props.theme.colors.primary};
  }
`
const ICON_WIDTH = 42
interface IconWrapperProps extends BorderProps {
  position: IconPosition
  borderRadius: string
  background: string
  hoverBackground?: string
  hoverIconColor?: string
  cursor?: CSSCursor
}
const IconWrapper = styled.div<IconWrapperProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-width: ${ICON_WIDTH}px;
  width: ${ICON_WIDTH}px;
  max-width: ${ICON_WIDTH}px;
  background: ${(props) => overloadColor(props.background)};

  ${getBorderStyle}
  border-radius: ${(props) =>
    props.position === "left"
      ? `${props.borderRadius} 0 0 ${props.borderRadius}`
      : `0 ${props.borderRadius} ${props.borderRadius} 0`};
  border-width: ${(props) =>
    props.position === "left" ? "1px 0 1px 1px" : "1px 1px 1px 0"};

  color: ${(props) =>
    contrastColor(overloadColor(props.background), "#eef", "#557", "#557")};

  cursor: ${(props) => props.cursor};

  &:hover {
    background: ${(props) => overloadColor(props.hoverBackground)};

    ${(props) =>
      props.hoverIconColor &&
      css`
        i {
          color: ${overloadColor(props.hoverIconColor)} !important;
        }
      `};
  }
`

export type TimeInputType = "hours" | "minutes" | "seconds"
type IconPosition = "left" | "right"
interface ExtendedIconProps extends IconProps {
  boxBackground?: string
  boxHoverBackground?: string
  boxHoverIconColor?: string
  boxCursor?: CSSCursor
}
export interface TimePickerProps extends BorderProps, BaseProps {
  value: Date
  error?: string | null
  errorTimeout?: number

  includeSeconds?: boolean
  disabled?: boolean
  disableWheel?: boolean
  separator?: string
  iconLeftProps?: ExtendedIconProps
  iconRightProps?: ExtendedIconProps

  width?: string
  minWidth?: string
  maxWidth?: string
  height?: string
  padding?: string
  color?: string
  background?: string
  fontSize?: string
  separatorColor?: string
  borderRadius?: string

  onErrorTimeout?: () => void
  onClick?: (
    type: TimeInputType | null,
    event: React.MouseEvent<HTMLInputElement | HTMLDivElement>,
  ) => void
  onWheel?: (
    type: TimeInputType,
    event: React.WheelEvent<HTMLInputElement>,
  ) => void
  onFocus?: (
    type: TimeInputType,
    event: React.FocusEvent<HTMLInputElement>,
  ) => void
  onBlur?: (
    type: TimeInputType,
    event: React.FocusEvent<HTMLInputElement>,
  ) => void
  onChange: (value: Date) => void
}

export const TimePicker: React.FC<TimePickerProps> = ({
  className,
  id,

  value,
  error,
  errorTimeout,

  includeSeconds = false,
  disabled = false,
  disableWheel = false,
  separator = ":",
  iconLeftProps,
  iconRightProps,

  width,
  minWidth,
  maxWidth,
  height = "32px",
  padding = "6px",
  color = "text.dark",
  background = "white",
  fontSize = "inherit",
  separatorColor = "text.gray",

  border,
  borderWidth,
  borderStyle,
  borderColor,
  borderRadius,

  onErrorTimeout = () => void 0,
  onClick = () => void 0,
  onWheel = () => void 0,
  onFocus = () => void 0,
  onBlur = () => void 0,
  onChange,
}) => {
  const hoursRef = useRef(value.getHours())
  const minutesRef = useRef(value.getMinutes())
  const secondsRef = useRef(value.getSeconds())

  const hoursInputRef = useRef<HTMLInputElement | null>(null)
  const minutesInputRef = useRef<HTMLInputElement | null>(null)
  const secondsInputRef = useRef<HTMLInputElement | null>(null)

  const theme = useTheme()
  const pad = (val: string | number) => (val + "").padStart(2, "0")

  const [internalHours, setInternalHours] = useState(() =>
    pad(value.getHours()),
  )
  const [internalMinutes, setInternalMinutes] = useState(() =>
    pad(value.getMinutes()),
  )
  const [internalSeconds, setInternalSeconds] = useState(() =>
    pad(value.getSeconds()),
  )

  const [internalError, setInternalError] = useState(error)

  useEffect(() => {
    setInternalError(error)
  }, [error])

  interface ChangeValues {
    hours?: number
    minutes?: number
    seconds?: number
  }
  const emitChange = useCallback(
    (values: ChangeValues) => {
      const h = values.hours ?? value.getHours()
      const m = values.minutes ?? value.getMinutes()
      const s = values.seconds ?? value.getSeconds()

      if (h < 0 || h > 23 || m < 0 || m > 59 || s < 0 || s > 59) return

      hoursRef.current = h
      minutesRef.current = m
      secondsRef.current = s

      const newDate = new Date(value.getTime())
      newDate.setHours(h, m, s)

      onChange(newDate)
    },
    [value, onChange],
  )

  const handleChange = useCallback(
    (type: TimeInputType) => {
      return (evt: React.ChangeEvent<HTMLInputElement>) => {
        let {
          target: { value },
        } = evt
        if (value.length > 2) value = value.slice(-2)
        else if (value === "") value = "0"
        if (!/^[0-9]*$/.test(value)) return

        const numValue = Number.parseInt(value, 10)
        if (numValue < 0) return // This isn't really possible though, but why not

        switch (type) {
          case "hours":
            if (numValue > 23) return
            emitChange({ hours: numValue })
            setInternalHours(() => numValue + "")
            if (value.length === 2 && minutesInputRef.current)
              minutesInputRef.current.select()
            break
          case "minutes":
            if (numValue > 59) return
            emitChange({ minutes: numValue })
            setInternalMinutes(() => numValue + "")
            if (includeSeconds && value.length === 2 && secondsInputRef.current)
              secondsInputRef.current.select()
            break
          case "seconds":
            if (numValue > 59) return
            emitChange({ seconds: numValue })
            setInternalSeconds(() => numValue + "")
            break
        }
      }
    },
    [includeSeconds, emitChange],
  )

  const handleClick = useCallback(
    (type: TimeInputType) => {
      return (evt: React.MouseEvent<HTMLInputElement>) => {
        onClick(type, evt)
        if (evt.defaultPrevented) return

        const { target } = evt
        if ("select" in (target as HTMLInputElement))
          (target as HTMLInputElement).select()
      }
    },
    [onClick],
  )

  const handleWheel = useCallback(
    (type: TimeInputType) => {
      return (evt: React.WheelEvent<HTMLInputElement>) => {
        onWheel(type, evt)
        if (evt.defaultPrevented || disableWheel) return

        const delta = evt.deltaY < 0 ? 1 : -1

        switch (type) {
          case "hours": {
            let hours = (hoursRef.current + delta) % 24
            hours < 0 && (hours = 23)
            emitChange({ hours })
            setInternalHours(() => pad(hoursRef.current))
            break
          }
          case "minutes": {
            let minutes = (minutesRef.current + delta) % 59
            minutes < 0 && (minutes = 59)
            emitChange({ minutes })
            setInternalMinutes(() => pad(minutesRef.current))
            break
          }
          case "seconds": {
            let seconds = (secondsRef.current + delta) % 59
            seconds < 0 && (seconds = 59)
            emitChange({ seconds })
            setInternalSeconds(() => pad(secondsRef.current))
            break
          }
        }
      }
    },
    [onWheel, disableWheel, emitChange],
  )

  const handleFocus = useCallback(
    (type: TimeInputType) => (evt: React.FocusEvent<HTMLInputElement>) => {
      onFocus(type, evt)
      if (evt.defaultPrevented) return

      const { target } = evt
      target.select()
    },
    [onFocus],
  )
  const handleBlur = useCallback(
    (type: TimeInputType) => (evt: React.FocusEvent<HTMLInputElement>) => {
      onBlur(type, evt)
      if (evt.defaultPrevented) return

      switch (type) {
        case "hours":
          setInternalHours(() => pad(hoursRef.current))
          break
        case "minutes":
          setInternalMinutes(() => pad(minutesRef.current))
          break
        case "seconds":
          setInternalSeconds(() => pad(secondsRef.current))
          break
      }
    },
    [onBlur],
  )

  const handleErrorTimeout = useCallback(() => {
    setInternalError(null)
    onErrorTimeout()
  }, [onErrorTimeout])

  const overrideIconClick = useCallback(
    (position: IconPosition, evt: React.MouseEvent<HTMLElement>) => {
      if (
        position === "left" &&
        !isUndefined(iconLeftProps) &&
        isFunction(iconLeftProps.onClick)
      )
        iconLeftProps.onClick(evt)
      else if (
        position === "right" &&
        !isUndefined(iconRightProps) &&
        isFunction(iconRightProps.onClick)
      )
        iconRightProps.onClick(evt)
    },
    [iconLeftProps, iconRightProps],
  )

  const parsedBorder = border ?? theme.layout.defaultBorder
  const parsedBorderRadius = borderRadius ?? theme.sizes.defaultBorderRadius

  const iconSide = useMemo(() => {
    if (isUndefined(iconLeftProps) && isUndefined(iconRightProps)) return "none"
    else if (isUndefined(iconLeftProps) && !isUndefined(iconRightProps))
      return "right"
    else if (!isUndefined(iconLeftProps) && isUndefined(iconRightProps))
      return "left"
    else return "both"
  }, [iconLeftProps, iconRightProps])

  return (
    <Wrapper
      className={className}
      id={id}
      height={height}
      color={color}
      fontSize={fontSize}
      separatorColor={separatorColor}
      onClick={(e) => onClick(null, e)}
    >
      <ComponentError
        className="--time-picker-error-wrapper"
        error={internalError}
        timeout={errorTimeout}
        onTimeout={handleErrorTimeout}
      />

      {iconLeftProps && (
        <IconWrapper
          className="--time-picker-left-icon-wrapper"
          position="left"
          color={color}
          background={iconLeftProps.boxBackground ?? background}
          hoverBackground={iconLeftProps.boxHoverBackground}
          hoverIconColor={iconLeftProps.boxHoverIconColor}
          cursor={iconLeftProps.boxCursor}
          border={parsedBorder}
          borderWidth={borderWidth}
          borderStyle={borderStyle}
          borderColor={borderColor}
          borderRadius={parsedBorderRadius}
          onClick={(e) => overrideIconClick("left", e)}
        >
          <Icon {...iconLeftProps} onClick={undefined} />
        </IconWrapper>
      )}

      <InnerWrapper
        className="--time-picker-inner-wrapper"
        iconSide={iconSide}
        width={width}
        minWidth={minWidth}
        maxWidth={maxWidth}
        padding={padding}
        border={parsedBorder}
        borderWidth={borderWidth}
        borderStyle={borderStyle}
        borderColor={borderColor}
        borderRadius={parsedBorderRadius}
        background={background}
      >
        <NumberInput
          className="--time-picker-input-hour"
          ref={hoursInputRef}
          value={internalHours}
          disabled={disabled}
          tabIndex={0}
          onClick={handleClick("hours")}
          onWheel={handleWheel("hours")}
          onFocus={handleFocus("hours")}
          onBlur={handleBlur("hours")}
          onChange={handleChange("hours")}
        />
        <span className="--time-picker-separator">{separator}</span>
        <NumberInput
          className="--time-picker-input-minute"
          ref={minutesInputRef}
          value={internalMinutes}
          disabled={disabled}
          tabIndex={0}
          onClick={handleClick("minutes")}
          onWheel={handleWheel("minutes")}
          onFocus={handleFocus("minutes")}
          onBlur={handleBlur("minutes")}
          onChange={handleChange("minutes")}
        />
        {includeSeconds && (
          <>
            <span className="--time-picker-separator">{separator}</span>
            <NumberInput
              className="--time-picker-input-second"
              ref={secondsInputRef}
              value={internalSeconds}
              disabled={disabled}
              tabIndex={0}
              onClick={handleClick("seconds")}
              onWheel={handleWheel("seconds")}
              onFocus={handleFocus("seconds")}
              onBlur={handleBlur("seconds")}
              onChange={handleChange("seconds")}
            />
          </>
        )}
      </InnerWrapper>

      {iconRightProps && (
        <IconWrapper
          className="--time-picker-right-icon-wrapper"
          position="right"
          color={color}
          background={iconRightProps.boxBackground ?? background}
          hoverBackground={iconRightProps.boxHoverBackground}
          hoverIconColor={iconRightProps.boxHoverIconColor}
          cursor={iconRightProps.boxCursor}
          border={parsedBorder}
          borderWidth={borderWidth}
          borderStyle={borderStyle}
          borderColor={borderColor}
          borderRadius={parsedBorderRadius}
          onClick={(e) => overrideIconClick("right", e)}
        >
          <Icon {...iconRightProps} onClick={undefined} />
        </IconWrapper>
      )}
    </Wrapper>
  )
}
